import { memo, useMemo, useEffect, useCallback, useState } from "react";
import { Card, Space, Tag, Typography, Alert, Row, Col, Statistic, Skeleton, Empty, Button, Popconfirm } from "antd";
import axios from "axios";
import { API_BASE_URL } from "../constants/config";
import { getAndSanitize } from '../shared/utils/sanitization';
import { HistorySection } from "./Users/HistorySection";
import ReceiptCard from "./ReceiptCard";
import type { WithdrawDetailsProps } from "./types"

export const WithdrawDetails: React.FC<WithdrawDetailsProps> = memo(({ 
  record, 
  onImageClick,
  onLoad,
  loading = false 
}) => {
  const handleLoad = useCallback(() => {
    onLoad?.('details');
  }, [onLoad]);

useEffect(() => {
}, [record]);

useEffect(() => {
  handleLoad();
}, [record, handleLoad]);

const historyData = useMemo(() => {
  const data = record.user?.historyData;
  return data;
}, [record.user?.historyData]);

  const availableBalance = useMemo(() => {
    const balanceStep = record.requestData?.processingSteps?.find(
      step => step.step === 'BALANCE_CHECK'
    );

    if (!balanceStep?.metadata) return 0;

    const balanceValue = balanceStep.metadata.balanceCheck?.available || 
                        balanceStep.metadata.available;

    if (!balanceValue) return 0;

    const parsedBalance = parseFloat(balanceValue);
    return isNaN(parsedBalance) ? 0 : parsedBalance;
  }, [record.requestData]);


  const cardTitle = useMemo(() => (
    <Space>
      <Typography.Text strong>Detalles del Retiro</Typography.Text>
      <Tag color={record.status === "COMPLETED" ? "success" : "processing"}>
        {record.status}
      </Tag>
    </Space>
  ), [record.status]);



  const totals = useMemo(() => {
    if (!record.user?.historyData) return {
      totalDeposits: 0,
      totalWithdraws: 0
    };


    return {
      totalDeposits: historyData
        .filter(record => record[3] === 'Credit Add')
        .reduce((sum, record) => sum + parseFloat(String(record[4]).replace(/\./g, '').replace(',', '.')), 0),
      totalWithdraws: historyData
        .filter(record => record[3] === 'Credit Out')
        .reduce((sum, record) => sum + parseFloat(String(record[4]).replace(/\./g, '').replace(',', '.')), 0)
    };
  }, [record.user?.historyData]);

  const descriptionItems = useMemo(() => [
    {
      label: "Balance Disponible",
      value: (
        <Typography.Text strong type="success">
          ${availableBalance.toFixed(2)}
        </Typography.Text>
      ),
    },
    {
      label: "Monto a Retirar",
      value: (
        <Typography.Text
          type={availableBalance < (record.withdrawAmount || 0) ? "danger" : undefined}
        >
          ${record.withdrawAmount?.toFixed(2)}
        </Typography.Text>
      ),
    },
    { 
      label: "Titular", 
      value: <Typography.Text copyable>{record.withdrawHolder}</Typography.Text> 
    },
    { 
      label: "CBU/CVU", 
      value: <Typography.Text copyable>{record.withdrawCvuCbu}</Typography.Text> 
    },
    { 
      label: "Alias", 
      value: <Typography.Text copyable>{record.withdrawAlias}</Typography.Text> 
    },
  ], [record, availableBalance]);

const warningAlert = useMemo(() => {
  // If request is rejected or canceled, show the rejection reason
  if (record.status === "REJECTED" || record.status === "CANCELED") {
    return (
      <Alert
        type="error"
        message={record.rejectionReason || "Retiro rechazado"}
        style={{ marginTop: "16px" }}
        showIcon
      />
    );
  }
  
  // Otherwise show balance warning if applicable
  if (availableBalance < (record.withdrawAmount || 0)) {
    return (
      <Alert
        type="warning"
        message="El monto de retiro excede el balance disponible"
        style={{ marginTop: "16px" }}
        showIcon
      />
    );
  }
  
  return null;
}, [availableBalance, record.withdrawAmount, record.status, record.rejectionReason]);

const [caminoBLoading, setCaminoBLoading] = useState(false);
const [caminoBDone, setCaminoBDone] = useState(false);

const rolloverCaminoB = useMemo(() => {
  return record.requestData?.rolloverCaminoB || record.rolloverCaminoB || null;
}, [record]);

const handleCaminoB = useCallback(async () => {
  if (!record.user?.id) return;
  setCaminoBLoading(true);
  try {
    const token = getAndSanitize('token');
    const adminId = record.adminId || record.user?.adminId || getAndSanitize('adminId') || getAndSanitize('userId');
    await axios.post(
      `${API_BASE_URL}/api/withdraw-requests/camino-b`,
      {
        userId: record.user.id,
        adminId,
        panelId: record.panelId || record.user?.panelId,
        username: record.user?.username,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCaminoBDone(true);
  } catch (error) {
    console.error('Error executing Camino B:', error);
  } finally {
    setCaminoBLoading(false);
  }
}, [record]);

  return (
    <div style={{ display: 'flex', gap: '16px', height: '100%' }}>
      {/* Left Column - Summary and History */}
      <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Card size="small" title="Resumen de Movimientos">
          <Row gutter={16}>
            <Col span={8}>
              <Statistic 
                title="Total Ingresado" 
                value={totals.totalDeposits} 
                precision={2} 
                prefix="$" 
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="Total Retirado" 
                value={totals.totalWithdraws} 
                precision={2} 
                prefix="$" 
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="Balance Total" 
                value={totals.totalDeposits - totals.totalWithdraws} 
                precision={2} 
                prefix="$" 
              />
            </Col>
          </Row>
        </Card>

        <Card 
          size="small" 
          title={
            <Space>
              <Typography.Text strong>Historial</Typography.Text>
              {record.user?.username && (
                <Typography.Text type="secondary">
                  ({record.user.username})
                </Typography.Text>
              )}
            </Space>
          }
          style={{ flex: '1', minHeight: '400px', overflow: 'hidden' }}
        >
{loading ? (
  <Skeleton active paragraph={{ rows: 4 }} />
) : historyData && Array.isArray(historyData) && historyData.length > 0 ? (
  <div style={{ height: 'calc(100% - 40px)', overflow: 'auto' }}>
<HistorySection 
  username={record.user?.username || 'Unknown'} // Add the required username prop
  historyData={historyData} 
  key={`history-${record.user?.username}`}
/>
  </div>
) : (
  <Empty description={
    <span>
      No hay datos históricos disponibles
      <br />
      <small>Usuario: {record.user?.username}</small>
    </span>
  } />
)}
        </Card>
      </div>

      {/* Right Column - Details and Receipt */}
<div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '16px' }}>
  <Card size="small" title={cardTitle} className="step-card">
    <Row gutter={[16, 8]}>
      {descriptionItems.map((item, index) => (
        <Col span={24} key={index}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography.Text type="secondary">{item.label}:</Typography.Text>
            {item.value}
          </div>
        </Col>
      ))}
    </Row>
    {warningAlert}
  </Card>

{rolloverCaminoB && !caminoBDone && (
  <Card size="small" title={
    <Space>
      <Typography.Text strong style={{ color: '#fa541c' }}>Rollover Pendiente</Typography.Text>
      <Tag color="orange">Camino B</Tag>
    </Space>
  }>
    <Row gutter={[16, 8]}>
      <Col span={24}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Text type="secondary">Retirable:</Typography.Text>
          <Typography.Text strong style={{ color: '#52c41a' }}>
            ${rolloverCaminoB.retirable?.toFixed(2) || '0.00'}
          </Typography.Text>
        </div>
      </Col>
      <Col span={24}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Text type="secondary">Bonus Pendiente:</Typography.Text>
          <Typography.Text strong style={{ color: '#fa541c' }}>
            ${rolloverCaminoB.bonusPendiente?.toFixed(2) || '0.00'}
          </Typography.Text>
        </div>
      </Col>
      <Col span={24}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Text type="secondary">Faltante de Rollover:</Typography.Text>
          <Typography.Text strong style={{ color: '#722ed1' }}>
            ${rolloverCaminoB.faltante?.toFixed(2) || '0.00'}
          </Typography.Text>
        </div>
      </Col>
    </Row>
    <div style={{ marginTop: 12 }}>
      <Alert
        type="warning"
        message={`El usuario puede retirar $${rolloverCaminoB.retirable?.toFixed(2) || '0.00'} renunciando al bonus de $${rolloverCaminoB.bonusPendiente?.toFixed(2) || '0.00'}`}
        style={{ marginBottom: 12 }}
        showIcon
      />
      <Popconfirm
        title="¿Ejecutar Camino B?"
        description="El usuario retirará solo el monto sin bonus. El rollover será cancelado."
        onConfirm={handleCaminoB}
        okText="Sí, ejecutar"
        cancelText="No"
      >
        <Button type="primary" danger block loading={caminoBLoading}>
          Ejecutar Camino B
        </Button>
      </Popconfirm>
    </div>
  </Card>
)}

{caminoBDone && (
  <Alert type="success" message="Camino B ejecutado exitosamente" showIcon />
)}

    <ReceiptCard
      loading={loading}
      record={{
        type: 'WITHDRAW',
        receipt: record.details?.receipt || record.receipt,
        withdraw: {
          receipt: record.details?.receipt || record.receipt
        },
        operation: {
          withdraw: {
            receipt: record.details?.receipt || record.receipt
          },
          receipt: record.details?.receipt || record.receipt
        }
      }}
      details={{
        receipt: record.details?.receipt || record.receipt
      }}
      onImageClick={onImageClick}
    />
    </div>

      <style >{`
        .receipt-container {
          width: 100%;
          height: 300px;
          overflow: hidden;
          border-radius: 4px;
          border: 1px solid #f0f0f0;
          transition: opacity 0.3s;
        }

        .receipt-container:hover {
          opacity: 0.8;
        }

        .receipt-card {
          margin-top: 16px;
        }

        .step-card {
          transition: all 0.3s;
        }
      `}</style>
    </div>
  );
});

WithdrawDetails.displayName = "WithdrawDetails";

export default WithdrawDetails;